import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, SlidersHorizontal, Zap, UtensilsCrossed } from "lucide-react";
import { getRecipes, getCuisine } from "@/lib/api";
import { CUISINE_ORDER, metaFor } from "@/lib/cuisines";
import { RecipeCard } from "@/components/RecipeCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PET_INFO = {
  key: "pet",
  name: "Pet-Friendly Kitchen",
  tagline: "Plain, safe meals for your furry friends",
  blurb:
    "A small collection of simple, unseasoned dishes that are safe for dogs and cats — no salt, oil, onion, garlic or spices, all of which can harm pets. Always cool the food before serving, and check with your vet before changing your pet's diet.",
};

export default function CuisinePage() {
  const { key } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get("q") || "";
  const isPet = key === "pet";

  const [cuisineFilter, setCuisineFilter] = useState(key || "all");
  const [query, setQuery] = useState(initialQ);
  const [recipes, setRecipes] = useState([]);
  const [cuisineInfo, setCuisineInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setCuisineFilter(key || "all");
    setQuery(searchParams.get("q") || "");
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (key === "pet") {
      setCuisineInfo(PET_INFO);
    } else if (key && key !== "all") {
      getCuisine(key).then(setCuisineInfo).catch(() => setCuisineInfo(null));
    } else {
      setCuisineInfo(null);
    }
  }, [key]);

  const fetchRecipes = useCallback(() => {
    setLoading(true);
    const params = {};
    if (cuisineFilter !== "all") params.cuisine = cuisineFilter;
    if (query.trim()) params.search = query.trim();
    getRecipes(params).then((data) => {
      setRecipes(data);
      setLoading(false);
    });
  }, [cuisineFilter, query]);

  useEffect(() => {
    const t = setTimeout(fetchRecipes, 250);
    return () => clearTimeout(t);
  }, [fetchRecipes]);

  const meta = cuisineInfo ? metaFor(cuisineInfo.key) : null;
  const heading = cuisineInfo ? cuisineInfo.name : query ? `Search results` : "All Recipes";

  return (
    <div data-testid="cuisine-page">
      {/* Header */}
      <section
        className={`border-b border-gray-100 ${cuisineInfo ? metaFor(cuisineInfo.key).sectionBg : "bg-gray-50"}`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {cuisineInfo && (
              <div className="flex items-center gap-3 mb-4">
                <span className={`h-2.5 w-2.5 rounded-full ${meta.dot}`} />
                <span className="text-xs font-bold uppercase tracking-[0.25em] text-gray-500">
                  {cuisineInfo.tagline}
                </span>
              </div>
            )}
            <h1 className="font-heading text-5xl sm:text-6xl font-light tracking-tighter text-gray-900">
              {heading}
              {query && <span className="italic"> “{query}”</span>}
            </h1>
            {cuisineInfo && (
              <p className="mt-6 max-w-2xl text-base md:text-lg text-gray-600 font-light leading-relaxed">
                {cuisineInfo.blurb}
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Controls */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-12">
        <div className="flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
          <div className="flex items-center border border-gray-200 focus-within:border-gray-900 transition-colors flex-1 max-w-sm">
            <Search className="h-4 w-4 text-gray-400 ml-3" />
            <input
              data-testid="cuisine-search-input"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSearchParams(e.target.value ? { q: e.target.value } : {});
              }}
              placeholder="Search by name or ingredient..."
              className="flex-1 px-2 py-2.5 text-sm outline-none bg-transparent"
            />
          </div>
          {!isPet && (
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4 text-gray-500" />
              <Select value={cuisineFilter} onValueChange={(v) => setCuisineFilter(v)}>
                <SelectTrigger
                  data-testid="cuisine-filter-select"
                  className="w-44 rounded-none border-gray-200"
                >
                  <SelectValue placeholder="Filter cuisine" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="filter-option-all">
                    All cuisines
                  </SelectItem>
                  {CUISINE_ORDER.map((k) => (
                    <SelectItem key={k} value={k} data-testid={`filter-option-${k}`}>
                      {metaFor(k).name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <p className="mt-6 text-sm text-gray-500" data-testid="recipe-count">
          {loading ? "Loading..." : `${recipes.length} recipe${recipes.length === 1 ? "" : "s"}`}
        </p>
      </div>

      {/* Grid split into Quick Dishes and Entrées */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-14">
        {!loading && recipes.length === 0 ? (
          <div
            data-testid="no-results"
            className="py-24 text-center border border-dashed border-gray-200"
          >
            <p className="font-heading text-3xl text-gray-900 mb-2">No recipes found</p>
            <p className="text-gray-500">Try a different dish name, ingredient or cuisine.</p>
          </div>
        ) : (
          <>
            <CategorySection
              title="Quick Dishes"
              subtitle="Ready in 15 minutes or less"
              icon={<Zap className="h-4 w-4" />}
              testid="section-quick"
              recipes={recipes.filter((r) => r.category === "quick")}
            />
            <CategorySection
              title="Entrées"
              subtitle="Heartier mains worth the wait"
              icon={<UtensilsCrossed className="h-4 w-4" />}
              testid="section-entree"
              recipes={recipes.filter((r) => r.category !== "quick")}
            />
          </>
        )}
      </section>
    </div>
  );
}

const CategorySection = ({ title, subtitle, icon, testid, recipes }) => {
  if (recipes.length === 0) return null;
  return (
    <div className="mb-16" data-testid={testid}>
      <div className="flex items-end justify-between border-b border-gray-200 pb-4 mb-8">
        <div className="flex items-center gap-3">
          <span className="text-gray-900">{icon}</span>
          <h2 className="font-heading text-3xl md:text-4xl font-medium text-gray-900">
            {title}
          </h2>
        </div>
        <span className="hidden sm:block text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">
          {subtitle}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
        {recipes.map((r, i) => (
          <RecipeCard key={r.id} recipe={r} index={i} />
        ))}
      </div>
    </div>
  );
};
